import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import type { ScenarioVerdict } from "../api";

const INK = "var(--chart-ink)";
const LIMIT = "var(--chart-failed)";
const GRID = "var(--chart-grid)";
const AXIS = "var(--chart-axis)";

type Point = { run_id: number; run_name: string; sim: number; thr: number; pass: boolean };

/** Per golden, oldest run first: the order a trend is read in. */
export function likenessSeries(items: ScenarioVerdict[]): [string, Point[]][] {
  const by = new Map<string, Point[]>();
  for (const it of [...items].reverse()) {
    for (const c of it.likeness ?? []) {
      const k = c.golden || "?";
      (by.get(k) ?? by.set(k, []).get(k)!).push({
        run_id: it.run_id, run_name: it.run_name, sim: c.similarity, thr: c.threshold, pass: c.pass,
      });
    }
  }
  return [...by.entries()].filter(([, pts]) => pts.length > 0);
}

const f5 = (v: number) => v.toFixed(5);

/**
 * How close each run came to the golden, against the threshold.
 *
 * A comparison is a number, and the pass/fail dot history hides it: run48
 * missed at 0.999481 against 0.9995, and the runs before it had passed at
 * 0.9996-something - one font update away. A line with the threshold drawn
 * under it shows the drift before the dot turns red. One series per golden, so
 * no legend: the heading names it. Every value is also in the table below the
 * chart, and each point says its run and score on hover.
 */
export function LikenessChart({ golden, points, bare = false }: { golden: string; points: Point[]; bare?: boolean }) {
  const { t } = useTranslation();
  const VW = 600, VH = 120, PL = 64, PR = 12, PT = 10, PB = 20;
  const vals = points.flatMap((p) => [p.sim, p.thr]);
  let lo = Math.min(...vals), hi = Math.max(...vals);
  const pad = Math.max((hi - lo) * 0.15, 0.00002);
  lo -= pad;
  hi = Math.min(1, hi + pad);
  const x = (i: number) => (points.length === 1 ? (PL + VW - PR) / 2 : PL + (i * (VW - PL - PR)) / (points.length - 1));
  const y = (v: number) => PT + ((hi - v) * (VH - PT - PB)) / (hi - lo || 1);
  const line = points.map((p, i) => `${i ? "L" : "M"}${x(i).toFixed(1)},${y(p.sim).toFixed(1)}`).join(" ");
  const thr = points[points.length - 1].thr;
  return (
    <figure className="mt-2" data-testid="likeness">
      {/* Under a heading that already says "likeness", the caption would say
          it a second time; the golden's name is then enough - and only when
          the card holds more than one chart. */}
      {!bare && (
        <figcaption className="text-xs text-slate-500">
          {t("likeness.title", { golden })}
        </figcaption>
      )}
      <svg viewBox={`0 0 ${VW} ${VH}`} className="mt-1 w-full max-w-2xl" role="img"
           aria-label={t("likeness.aria", { golden, count: points.length })}>
        {[hi, (hi + lo) / 2, lo].map((v) => (
          <g key={v}>
            <line x1={PL} x2={VW - PR} y1={y(v)} y2={y(v)} stroke={GRID} />
            <text x={PL - 6} y={y(v) + 3} fontSize={10} textAnchor="end" fill={INK}>{f5(v)}</text>
          </g>
        ))}
        <line x1={PL} x2={VW - PR} y1={VH - PB} y2={VH - PB} stroke={AXIS} />
        <line x1={PL} x2={VW - PR} y1={y(thr)} y2={y(thr)} stroke={LIMIT} strokeWidth={1.5} strokeDasharray="5 4" />
        <text x={VW - PR} y={y(thr) - 4} fontSize={10} textAnchor="end" fill={LIMIT}>
          {t("likeness.threshold", { value: f5(thr) })}
        </text>
        <path d={line} fill="none" stroke={INK} strokeWidth={2} strokeLinejoin="round" />
        {points.map((p, i) => (
          <g key={`${p.run_id}-${i}`}>
            <circle cx={x(i)} cy={y(p.sim)} r={4} fill={p.pass ? INK : LIMIT} stroke="var(--surface, #fff)" strokeWidth={2} />
            {/* The hit target is bigger than the mark. */}
            <circle cx={x(i)} cy={y(p.sim)} r={10} fill="transparent">
              <title>{t("likeness.point", { run: p.run_name, score: f5(p.sim), threshold: f5(p.thr) })}</title>
            </circle>
          </g>
        ))}
        <text x={PL} y={VH - 5} fontSize={10} fill={INK}>{points[0].run_name}</text>
        {points.length > 1 && (
          <text x={VW - PR} y={VH - 5} fontSize={10} textAnchor="end" fill={INK}>
            {points[points.length - 1].run_name}
          </text>
        )}
      </svg>
      {/* One score in every run is a steady difference - a font, a layout -
          not flicker, and it is the first thing worth knowing about a miss. */}
      {points.length > 1 && Math.max(...points.map((p) => p.sim)) - Math.min(...points.map((p) => p.sim)) < 1e-9 && (
        <p className="text-sm text-slate-600 dark:text-slate-400" data-testid="likeness-flat">{t("likeness.flat")}</p>
      )}
      <details className="text-xs text-slate-500">
        <summary className="cursor-pointer">{t("likeness.table")}</summary>
        <table className="mt-1 text-xs">
          <tbody>
            {[...points].reverse().map((p, i) => (
              <tr key={`${p.run_id}-${i}`}>
                <td className="pr-3"><Link className="underline" to={`/runs/${p.run_id}`}>{p.run_name}</Link></td>
                <td className="pr-3 text-right tabular-nums">{f5(p.sim)}</td>
                <td className="pr-3 text-right tabular-nums">{f5(p.thr)}</td>
                <td>{p.pass ? t("likeness.passed") : t("likeness.missed")}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </details>
    </figure>
  );
}

export default function Likeness({ items, titled = false }: { items: ScenarioVerdict[]; titled?: boolean }) {
  const series = likenessSeries(items);
  if (series.length === 0) return null;
  return (
    <>
      {series.map(([g, pts]) => <LikenessChart key={g} golden={g} points={pts} bare={titled && series.length === 1} />)}
    </>
  );
}
